/* eslint-disable react/jsx-key */
import { Text } from "@chakra-ui/react";
import { Formatter, useCMSBlogs, useMobile } from "@with-nx/hooks-n-helpers";
import {
  DesignedButton,
  DesignedCard,
  DesignedIcon,
  Rule,
} from "@with-nx/simple-ui/atoms";
import { ReusableSection } from "@with-nx/simple-ui/atoms";
import {
  AutoScroller,
  CatalogLayout,
  FeaturesContainer,
  GridBlog,
  GridStepScroller,
  ReusableContainerPartners,
  ReusableGradientHero,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import { Box } from "simple-effing-primitive-layout";
import Image from "next/image";
import { useRegion } from "@with-nx/region";

const TimelineGraphic = () => {
  return (
    <AutoScroller
      top={0}
      manual={true}
      background="var(--background)"
      paddings={50}
      duration={20}
    >
      <img
        src="/assets/section/timeline.webp"
        alt="Broadway Media Timeline"
        style={{
          width: 6942,
          minWidth: 6942,
          height: 400,
          display: "block",
          pointerEvents: "none",
          mixBlendMode: "lighten",
          backgroundColor: "var(--background)",
        }}
        loading="lazy"
      />
    </AutoScroller>
  );
};

export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};

export default function Company({ resolvedUrl }) {
  const blogs = useCMSBlogs({
    page: 0,
    per: 3,
  });
  const mobile = useMobile();

  const region = useRegion();

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar={true}
      title="Broadway Media - Our Company"
      description="Broadway Media’s team of theatre-makers are driven by our passion and our purpose to strengthen the performing arts community"
      resolvedUrl={resolvedUrl}
    >
      <ReusableGradientHero
        slanted={false}
        src="/assets/hero/homepage-background-image.jpg"
        backdrop="/assets/hero/homepage-gradient-webp.webp"
      >
        <Rule parse="d:block" tag="h1">
          <Rule parse="!_t d:block mb:12" tag="span">
            Hi, we're Broadway Media
          </Rule>
          <Rule parse="!_p d:block mb:32" tag="span">
            We specialize in technology that serves theatres worldwide.
          </Rule>
        </Rule>

        {mobile ? undefined : (
          <Box
            parse="d:flex a:center fw:wrap w:wrap"
            direction={mobile ? "column" : "row"}
          >
            <DesignedButton
              label="Contact Us"
              theme="white"
              href="/contact"
              properties={
                !mobile
                  ? { right: 10 }
                  : {
                      style: {
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }
              }
              container={
                !mobile
                  ? undefined
                  : {
                      style: {
                        width: "100%",
                        display: "block",
                      },
                    }
              }
            />
            <Box parse="h:40">
              <Rule parse="d:block mb:2 !lt">Contact via social media</Rule>
              <Text className="inline-flex justify-center sm:justify-start space-x-4">
                <a
                  href={
                    region?.meta?.facebook ||
                    "https://www.facebook.com/broadwaymedia"
                  }
                  target="_blank"
                  className="text-brand-entity"
                  rel="nofollow"
                >
                  <DesignedIcon override="white" name="@facebook" />
                </a>
                <a
                  href={
                    region?.meta?.instagram ||
                    "https://www.instagram.com/broadwaymedia"
                  }
                  target="_blank"
                  rel="nofollow"
                >
                  <DesignedIcon override="white" name="@instagram" />
                </a>

                <a
                  target="_blank"
                  href="https://www.youtube.com/channel/UCtxeMy2zYcwBiszlOYqDedg"
                  rel="nofollow"
                >
                  <DesignedIcon override="white" name="@youtube" />
                </a>
                <a
                  href="https://www.linkedin.com/company/broadwaymedia"
                  target="_blank"
                  rel="nofollow"
                >
                  <DesignedIcon override="white" name="@linkedin" />
                </a>
              </Text>
            </Box>
          </Box>
        )}
      </ReusableGradientHero>

      <ReusableSection>
        <div className="container">
          <Row align="middle" justify="center">
            <Col xs={24} sm={24} md={18} lg={18}>
              <Rule
                parse={
                  mobile
                    ? "!ds d:block mb:24 ta:center"
                    : "!hl d:block mb:4 ta:center"
                }
              >
                We are a mission-driven company whose decision making centers
                around one simple question:
              </Rule>
              <Rule
                parse={
                  mobile
                    ? "!hl d:block mb:0 c:#2F80ED ta:center"
                    : "!hm d:block mb:0 c:#2F80ED ta:center"
                }
              >
                Does it make participation in the performing arts more
                accessible?
              </Rule>
            </Col>
          </Row>
        </div>
      </ReusableSection>
      <ReusableSection>
        <TimelineGraphic />
      </ReusableSection>
      <div className="container">
        <ReusableSection>
          <Row justify="center">
            <Col xs={24} md={24} lg={18}>
              <DesignedCard
                size="16x9"
                embed="https://www.youtube.com/watch?v=Qx5RdK1N9KM&feature=youtu.be"
                muted={false}
                loop={false}
                controls={true}
                playing={false}
              />
            </Col>
          </Row>
        </ReusableSection>
      </div>

      <ReusableSection>
        <div className="container">
          <Rule parse="!hl d:block mb:36" tag="h3">
            The Broadway Media Experience
          </Rule>
        </div>
        <FeaturesContainer
          xl={6}
          lg={6}
          md={6}
          items={[
            {
              title: "Full Suite of Services",
              description:
                "We aim to provide you with innovative and affordable resources to make your vision a reality",
            },
            {
              title: "Community Focused",
              description:
                "We give back to our community trough grants, scholarships, and royalties paid to artists on every sale",
            },
            {
              title: "Production Experts",
              description:
                "We're part of your production team, happily on stand-by and ready to assists when you need us",
            },
            {
              title: "Theatre-Makers",
              description:
                "We have years of experience producing live shows, and know first-hand the needs of the stage",
            },
          ]}
        />
      </ReusableSection>

      <ReusableSection>
        <ReusableContainerPartners slanted={false} />
      </ReusableSection>

      <div className="container">
        <ReusableSection>
          <div className="container">
            <Row gutter={[10, 0]}>
              <Col xs={24} sm={24} md={12}>
                <Rule parse="!_t d:block mb:24 c:#FFF" tag="h4">
                  Our Story
                </Rule>
                <Rule parse="!pm d:block mb:24 c:#FFF">
                  Our company was founded in 2013 by Quentin Sanford. Sanford
                  grew up in the theatre (he’ll tell you he spent more time
                  there than at school), and as a young entrepreneur would spend
                  his time creating show posters for theaters in the surrounding
                  area. His time spent in community theatre gave him an
                  appreciation for the industriousness of theatre makers faced
                  with limited budgets. Years later, determined to help the sort
                  of theaters he participated in as a child, and inspired by the
                  increasingly common integration of video projections in
                  Broadway, he founded Broadway Motion Design, re-imagining the
                  video projection instead as an affordable resource for
                  theatre.
                </Rule>
                <Rule parse="!pm d:block mb:24 c:#FFF">
                  Since the launch of our first scenic product, we’ve spent
                  years refining and engaging the theatre community to identify
                  the needs of smaller theaters and educational organizations,
                  leading us to pursuing licensor-approved scenery, intuitive
                  software, and - eventually - hardware solutions. With these
                  three resources, and our new partnerships with theatrical
                  licensors, we grew our team and re-branded as Broadway Media
                  Distribution, Inc.
                </Rule>
                <Rule parse="!pm d:block mb:24 c:#FFF">
                  In 2019, now as a larger resource provider, we began to
                  re-imagine what our company could do. With new relationships
                  with Broadway designers - our first being Aaron Rhyne - we saw
                  that we could elevate this creative talent to a huge
                  collection of theatre makers they had otherwise never
                  considered, we could protect designers’ intellectual property,
                  and we could utilize our distribution network to make amazing
                  design available to everyone.
                </Rule>
                <Rule parse="!pm d:block mb:24 c:#FFF">
                  Today, driven by our mission to make theatre more accessible -
                  for theatrical organizations, schools, and designers, we
                  continue to diversify the resources we offer and are now
                  known, simply, as Broadway Media.
                </Rule>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Image
                  src="/assets/team/our_history.png"
                  loading="lazy"
                  objectFit="contain"
                  width={mobile ? 300 : 776}
                  height={mobile ? 300 : 700}
                  className="responsive-radius-image"
                />
              </Col>
            </Row>
          </div>
        </ReusableSection>
      </div>

      <div className="container">
        <Row gutter={[36, 0]}>
          <Col xs={24}>
            <Rule parse="!ds d:block mb:24 pt:0" tag="h5">
              Interested in joining our global team?
            </Rule>
            <Rule parse="!ll d:block mb:12">
              <DesignedButton label="See Open Positions" href="/careers" />
            </Rule>
          </Col>
        </Row>
      </div>

      <ReusableSection>
        <div className="container">
          <Row gutter={[36, 36]}>
            <Col xs={24} md={12}>
              <Rule parse="!ds d:block mb:24" tag="h5">
                What We Offer
              </Rule>
              <Rule parse="!pl d:block mb:12">
                At Broadway Media, we believe creativity thrives when artists
                and educators have the resources they need. Our digital platform
                is designed for community, educational, and independent theatre
                makers, created by theatre professionals with real industry
                experience. We feature the world's largest selection of
                officially-sanctioned, Broadway-quality resources, tailored to
                make producing theatre more accessible and affordable.
              </Rule>
            </Col>
          </Row>
        </div>
      </ReusableSection>

      <ReusableSection background="transparent">
        <div className="container">
          <Rule
            rule="hl"
            color="var(--font1)"
            bottom={24}
            display="block"
            tag="h6"
          >
            Recent news about Broadway Media
          </Rule>
          <GridStepScroller
            xs={24}
            sm={24}
            md={12}
            lg={8}
            xl={8}
            items={blogs.blogs.map((blog, b) => {
              if (b > 2) {
                return null;
              }

              return (
                <GridBlog
                  key={blog._id}
                  date={blog.date}
                  description={blog.description}
                  image={Formatter.compressed(blog.coverImage, 256, true)}
                  title={blog.title}
                  url={`/blog/${blog.slug}`}
                  transparent={false}
                  properties={{
                    height: "100%",
                  }}
                />
              );
            })}
          />
          <Box css="d:flex j:flex-end" parse="mt:24">
            <DesignedButton
              size="small"
              theme="tetriary"
              label="See More Stories"
              href={(() => {
                if (typeof window !== "undefined") {
                  const _url = new URL(`${window.location.origin}/blog`);
                  _url.search = new URLSearchParams({
                    filter: "Press Release",
                  }).toString();

                  return _url.toString();
                }

                return "";
              })()}
            />
          </Box>
        </div>
      </ReusableSection>
    </CatalogLayout>
  );
}
